import { useEffect, useState } from "react";
import {
  Box,
  Tab,
  Tabs,
  Typography,
  Divider,
  Alert,
  Button,
  CircularProgress,
  useTheme,
  alpha,
} from "@mui/material";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import {
  getRedirectResult,
  OAuthCredential,
  AuthError,
} from "firebase/auth";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { auth, authPersistenceReady, googleProvider, microsoftProvider, facebookProvider } from "../firebase";
import { useAuthStore } from "../store/authSlice";
import { useAuth } from "../context/AuthContext";
import MagicLinkForm from "../components/auth/MagicLinkForm";
import { config } from "../config";
import {
  getPostAuthDestination,
  syncMarketingPlanInterestFromSearchParams,
} from "../lib/billingSelection";
import { trackAuthComplete, trackAuthStart, trackMarketingPlanInterestCaptured } from "../lib/analytics";
import { startProviderSignIn } from "../lib/providerAuth";

/* ─── Provider icons ─────────────────────────────────────────────────────── */

function GoogleIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z"/>
      <path fill="#34A853" d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z"/>
      <path fill="#FBBC05" d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.175 0 7.55 0 9s.348 2.825.957 4.039l3.007-2.332z"/>
      <path fill="#EA4335" d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z"/>
    </svg>
  );
}

function MicrosoftIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 18 18" aria-hidden="true">
      <rect x="0" y="0" width="8.5" height="8.5" fill="#F35325"/>
      <rect x="9.5" y="0" width="8.5" height="8.5" fill="#81BC06"/>
      <rect x="0" y="9.5" width="8.5" height="8.5" fill="#05A6F0"/>
      <rect x="9.5" y="9.5" width="8.5" height="8.5" fill="#FFBA08"/>
    </svg>
  );
}

function FacebookIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#1877F2" d="M18 9a9 9 0 1 0-10.406 8.892V11.39H5.309V9h2.285V7.012c0-2.257 1.344-3.504 3.4-3.504.984 0 2.014.176 2.014.176V5.92h-1.134c-1.118 0-1.467.694-1.467 1.406V9h2.496l-.399 2.39h-2.097v6.502A9.003 9.003 0 0 0 18 9z"/>
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
      <rect x="2" y="4" width="20" height="16" rx="2"/>
      <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
    </svg>
  );
}

/* ─── Auth provider button ────────────────────────────────────────────────── */

interface ProviderBtnProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  loading?: boolean;
  disabled?: boolean;
}

function ProviderBtn({ icon, label, onClick, loading = false, disabled = false }: ProviderBtnProps) {
  return (
    <Button
      fullWidth
      variant="outlined"
      onClick={onClick}
      disabled={disabled || loading}
      startIcon={loading ? <CircularProgress size={18} color="inherit" /> : icon}
      sx={{
        minHeight: 48,
        borderRadius: 1.25,
        borderColor: "divider",
        color: "text.primary",
        fontWeight: 500,
        fontSize: "0.875rem",
        textTransform: "none",
        justifyContent: "flex-start",
        px: 2,
        mb: 1.5,
        backgroundColor: "background.paper",
        boxShadow: "none",
        "&:hover": {
          backgroundColor: "background.default",
          borderColor: "secondary.light",
          boxShadow: "none",
        },
        "&:active": { backgroundColor: "background.default" },
        "&.Mui-focusVisible": {
          outline: "3px solid",
          outlineColor: "primary.light",
          outlineOffset: 2,
        },
        "&.Mui-disabled": { opacity: 0.5 },
      }}
    >
      {label}
    </Button>
  );
}

/* ─── Feature item ────────────────────────────────────────────────────────── */

function CapabilityItem({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          flex: "0 0 auto",
          borderRadius: 1,
          color: "primary.light",
          backgroundColor: (theme) => alpha(theme.palette.primary.light, 0.12),
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ color: "common.white", fontWeight: 600, fontSize: "0.875rem", lineHeight: 1.4 }}>
        {title}
      </Typography>
    </Box>
  );
}

/* ─── Provider flags ─────────────────────────────────────────────────────── */

const ENABLE_MICROSOFT = config.featureFlags.enableMicrosoftAuth;
const ENABLE_FACEBOOK = config.featureFlags.enableFacebookAuth;

/* ─── Main component ──────────────────────────────────────────────────────── */

export default function AuthScreen() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { mode, setMode, setLinkProviderChallenge } = useAuthStore();
  const { currentUser, initializing } = useAuth();
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null);
  const [resolvingRedirect, setResolvingRedirect] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const suspendedError = searchParams.get("error") === "suspended";

  useEffect(() => {
    const marketingPlanInterest = syncMarketingPlanInterestFromSearchParams(searchParams);
    if (marketingPlanInterest) {
      trackMarketingPlanInterestCaptured(marketingPlanInterest, location.pathname);
    }

    const requestedMode = searchParams.get("mode");
    if (location.pathname === "/register" || requestedMode === "create") {
      setMode("create");
      return;
    }
    if (requestedMode === "signin") {
      setMode("signin");
    }
  }, [location.pathname, searchParams, setMode]);

  useEffect(() => {
    let active = true;

    const resolveRedirect = async () => {
      try {
        await authPersistenceReady;
        if (!active) return;

        const result = await getRedirectResult(auth);
        if (!active) return;

        if (result?.user) {
          trackAuthComplete(result.providerId ?? "provider_redirect", location.pathname);
          return;
        }
      } catch (err) {
        if (!active) return;

        const authErr = err as AuthError;
        if (authErr.code === "auth/account-exists-with-different-credential") {
          const email = (authErr.customData?.email as string) ?? "";
          const tokenResponse = (authErr.customData as Record<string, unknown>)?.["_tokenResponse"];
          const credential = tokenResponse
            ? OAuthCredential.fromJSON(tokenResponse as Parameters<typeof OAuthCredential.fromJSON>[0])
            : null;
          if (credential) {
            setLinkProviderChallenge(email, credential);
            navigate("/link-provider", { replace: true });
            return;
          }
        }

        setError(authErr.message ?? "Sign in failed. Please try again.");
      } finally {
        if (active) {
          setResolvingRedirect(false);
        }
      }
    };

    void resolveRedirect();

    return () => {
      active = false;
    };
  }, [location.pathname, navigate, setLinkProviderChallenge]);

  useEffect(() => {
    if (resolvingRedirect || initializing || !currentUser) {
      return;
    }

    navigate(getPostAuthDestination(), { replace: true });
  }, [currentUser, initializing, navigate, resolvingRedirect]);

  const handleProviderSignIn = async (
    provider: typeof googleProvider | typeof microsoftProvider | typeof facebookProvider,
    name: string
  ) => {
    setError(null);
    setLoadingProvider(name);
    trackAuthStart(name);
    try {
      await startProviderSignIn(provider);
    } catch (err) {
      const authErr = err as AuthError;
      if (authErr.code === "auth/account-exists-with-different-credential") {
        const email = (authErr.customData?.email as string) ?? "";
        const tokenResponse = (authErr.customData as Record<string, unknown>)?.["_tokenResponse"];
        const credential = tokenResponse
          ? OAuthCredential.fromJSON(tokenResponse as Parameters<typeof OAuthCredential.fromJSON>[0])
          : null;
        if (credential) {
          setLinkProviderChallenge(email, credential);
          navigate("/link-provider");
          return;
        }
      }
      setError(authErr.message ?? "Sign in failed. Please try again.");
    } finally {
      setLoadingProvider(null);
    }
  };

  const ambientGlow = `radial-gradient(circle at 18% 14%, ${alpha(theme.palette.primary.light, 0.2)} 0, transparent 32%)`;
  const showAuthResolutionState = resolvingRedirect || (!!loadingProvider && !currentUser);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: { xs: "block", sm: "grid" },
        gridTemplateColumns: {
          sm: "minmax(310px, 5fr) minmax(390px, 7fr)",
          lg: "minmax(520px, 5fr) minmax(560px, 7fr)",
        },
        overflowX: "hidden",
        backgroundColor: "background.default",
        "@supports (height: 100dvh)": { minHeight: "100dvh" },
      }}
    >
      <Box
        component="section"
        aria-labelledby="setin-auth-heading"
        sx={{
          minWidth: 0,
          minHeight: { xs: 222, sm: "100vh" },
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          position: "relative",
          overflow: "hidden",
          backgroundColor: "secondary.dark",
          backgroundImage: ambientGlow,
          color: "common.white",
          px: { xs: 3, sm: 4, md: 6, lg: 9 },
          py: { xs: 3, sm: 6 },
          "@supports (height: 100dvh)": { minHeight: { xs: 222, sm: "100dvh" } },
        }}
      >
        <Box
          aria-hidden="true"
          sx={{
            position: "absolute",
            right: { xs: 3, sm: 0 },
            bottom: 0,
            width: { xs: 72, sm: 132, lg: 180 },
            height: { xs: 3, sm: 4 },
            backgroundColor: "primary.main",
            pointerEvents: "none",
          }}
        />
        <Box
          component="img"
          src="/media/setin_logo.png"
          alt="Setin"
          sx={{
            width: { xs: 132, sm: 148, lg: 176 },
            height: "auto",
            display: "block",
            mb: { xs: 2.5, sm: 5, lg: 7 },
          }}
        />
        <Typography
          id="setin-auth-heading"
          component="h1"
          sx={{
            maxWidth: 590,
            fontSize: { xs: "1.75rem", sm: "2.125rem", lg: "3rem" },
            fontWeight: 700,
            lineHeight: { xs: 1.16, lg: 1.12 },
            mb: { xs: 1.25, sm: 2.5 },
          }}
        >
          <Box component="span" sx={{ color: "primary.light" }}>
            Stay with the client.
          </Box>
          <br />
          Setin handles the conversation.
        </Typography>
        <Typography
          sx={{
            display: { xs: "none", sm: "block" },
            maxWidth: 560,
            color: (currentTheme) => alpha(currentTheme.palette.common.white, 0.76),
            fontSize: { sm: "0.875rem", lg: "1rem" },
            lineHeight: 1.7,
            mb: { sm: 4, lg: 5 },
          }}
        >
          Setin answers calls and messages, explains services, checks availability and creates
          bookings while you focus on the client in front of you.
        </Typography>
        <Box sx={{ display: { xs: "none", sm: "grid" }, gap: 2 }}>
          <CapabilityItem
            icon={<ChatBubbleOutlineRoundedIcon fontSize="small" />}
            title="Answers calls and messages"
          />
          <CapabilityItem
            icon={<AutoAwesomeRoundedIcon fontSize="small" />}
            title="Guides customers through services"
          />
          <CapabilityItem
            icon={<EventAvailableRoundedIcon fontSize="small" />}
            title="Creates bookings and records outcomes"
          />
        </Box>
      </Box>

      <Box
        component="main"
        sx={{
          minWidth: 0,
          minHeight: { sm: "100vh" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "background.default",
          px: { xs: 2.5, sm: 3, md: 5, lg: 7 },
          py: { xs: 4, sm: 5 },
          "@supports (height: 100dvh)": { minHeight: { sm: "100dvh" } },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 440,
            backgroundColor: "background.paper",
            border: { xs: "none", sm: "1px solid" },
            borderColor: "divider",
            borderRadius: { xs: 0, sm: 1.5 },
            boxShadow: {
              xs: "none",
              sm: (currentTheme) =>
                "0 18px 50px " + alpha(currentTheme.palette.secondary.main, 0.08),
            },
            p: { xs: 0, sm: 4, md: 5 },
          }}
        >
          {showAuthResolutionState ? (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <CircularProgress sx={{ mb: 3 }} />
              <Typography
                component="h2"
                sx={{ fontWeight: 700, fontSize: "1.35rem", color: "text.primary", mb: 0.75 }}
              >
                Returning to Setin
              </Typography>
              <Typography sx={{ color: "text.secondary", fontSize: "0.875rem", lineHeight: 1.6 }}>
                Finishing your sign-in and restoring your portal session.
              </Typography>
            </Box>
          ) : (
            <>
              <Tabs
                value={mode}
                onChange={(_, v) => { setMode(v); setError(null); setShowEmailForm(false); }}
                aria-label="Authentication mode"
                sx={{
                  mb: 3,
                  minHeight: 44,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                  "& .MuiTabs-indicator": { backgroundColor: "primary.main", height: 3 },
                  "& .MuiTab-root": {
                    minHeight: 44,
                    fontWeight: 600,
                    fontSize: "0.8125rem",
                    letterSpacing: 0,
                    textTransform: "none",
                    color: "text.secondary",
                    px: 0,
                    mr: 3,
                    "&.Mui-selected": { color: "text.primary" },
                    "&.Mui-focusVisible": {
                      outline: "3px solid",
                      outlineColor: "primary.light",
                      outlineOffset: -3,
                    },
                  },
                }}
              >
                <Tab label="Sign in" value="signin" disableRipple />
                <Tab label="Create account" value="create" disableRipple />
              </Tabs>

              <Typography
                component="h2"
                sx={{ fontWeight: 700, fontSize: "1.5rem", color: "text.primary", mb: 0.5 }}
              >
                {mode === "signin" ? "Welcome back" : "Create your account"}
              </Typography>
              <Typography sx={{ color: "text.secondary", fontSize: "0.875rem", mb: 3.5 }}>
                {mode === "signin" ? "Sign in to your Setin account" : "Start your 30-day trial. No card required."}
              </Typography>

              {/* Alerts */}
              {suspendedError && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                  Your account has been suspended. Please contact support.
                </Alert>
              )}
              {error && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {/* Social providers */}
              <ProviderBtn
                icon={<GoogleIcon />}
                label="Continue with Google"
                onClick={() => handleProviderSignIn(googleProvider, "google")}
                loading={loadingProvider === "google"}
                disabled={!!loadingProvider}
              />
              {ENABLE_MICROSOFT && (
                <ProviderBtn
                  icon={<MicrosoftIcon />}
                  label="Continue with Microsoft"
                  onClick={() => handleProviderSignIn(microsoftProvider, "microsoft")}
                  loading={loadingProvider === "microsoft"}
                  disabled={!!loadingProvider}
                />
              )}
              {ENABLE_FACEBOOK && (
                <ProviderBtn
                  icon={<FacebookIcon />}
                  label="Continue with Facebook"
                  onClick={() => handleProviderSignIn(facebookProvider, "facebook")}
                  loading={loadingProvider === "facebook"}
                  disabled={!!loadingProvider}
                />
              )}

              <Divider sx={{ my: 2, color: "text.secondary", fontSize: "0.75rem" }}>or</Divider>

              {showEmailForm ? (
                <MagicLinkForm />
              ) : (
                <ProviderBtn
                  icon={<EmailIcon />}
                  label="Continue with Email"
                  onClick={() => {
                    trackAuthStart("email_link");
                    setShowEmailForm(true);
                  }}
                  disabled={!!loadingProvider}
                />
              )}

            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
